if (typeof QuickVariantDrawer !== 'function') {

  class QuickVariantDrawer extends HTMLElement {

    constructor() {
      super();
      this._boundKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this.singleVariant = this.dataset.singleVariant === 'true';
      this.drawerVariants = this.querySelector('.variant-drawer__body product-variants');
      this.drawerFormHost = this.querySelector('product-form');
      this.drawerForm = this.drawerFormHost && this.drawerFormHost.querySelector('form');
      this.drawerMedia = this.querySelector('[data-js-drawer-media]');
      this.drawerQty = this.querySelector('[data-js-product-quantity] input[name="quantity"]');
      this.atcBtn = this.querySelector('[data-js-quick-drawer-atc]');
      this.atcText = this.querySelector('[data-js-quick-drawer-atc-text]');
      this.buyNowBtn = this.querySelector('[data-js-quick-drawer-buy-now]');

      this._ensureVariantIdInput();

      this.querySelectorAll('[data-js-drawer-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.atcBtn?.addEventListener('click', () => this._onAtcClick());
      this.buyNowBtn?.addEventListener('click', () => this._onBuyNowClick());

      if (this.drawerVariants) {
        this.drawerVariants.addEventListener('VARIANT_CHANGE', () => this._updateUi());
        this.drawerVariants.addEventListener('change', () => this._updateUi());
        this.drawerVariants.addEventListener('input', () => {
          if (typeof this.drawerVariants.updateVariantOptionLabels === 'function') {
            this.drawerVariants.updateVariantOptionLabels();
          }
        });
      }

      this._updateUi();
    }

    _ensureVariantIdInput() {
      if (!this.drawerForm) {
        return null;
      }

      this.drawerForm.querySelector('template')?.remove();

      let input = this.drawerForm.querySelector('input[name="id"][data-js-variant-id-input]');
      if (!input) {
        input = this.drawerForm.querySelector('input[name="id"]');
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'id';
        input.setAttribute('data-js-variant-id-input', '');
        this.drawerForm.prepend(input);
      }

      const variant = this._getActiveVariant();
      if (variant && !input.value) {
        input.value = variant.id;
      }

      return input;
    }

    _needsVariantSelection() {
      if (this.singleVariant) {
        return false;
      }
      return (
        this.drawerVariants &&
        this.drawerVariants.hasAttribute('data-variant-required') &&
        !this.drawerVariants.classList.contains('variant-selected')
      );
    }

    _getActiveVariant() {
      if (this.drawerVariants && this.drawerVariants.currentVariant) {
        return this.drawerVariants.currentVariant;
      }
      if (this.singleVariant && this.drawerVariants && typeof this.drawerVariants.getVariantData === 'function') {
        const variants = this.drawerVariants.getVariantData();
        if (variants?.length) {
          return variants.find((item) => item.available) || variants[0];
        }
      }
      return null;
    }

    _syncFormVariantId() {
      const variant = this._getActiveVariant();
      const idInput = this._ensureVariantIdInput();
      if (idInput && variant) {
        idInput.value = variant.id;
      }
    }

    _updateUi() {
      const variant = this._getActiveVariant();
      const needsSelection = this._needsVariantSelection();
      const hasVariant = Boolean(variant) || this.singleVariant;
      const canPurchase = hasVariant && (!variant || variant.available);

      this._syncFormVariantId();

      [this.atcBtn, this.buyNowBtn].forEach((btn) => {
        if (!btn) {
          return;
        }
        btn.classList.remove('disabled', 'needs-variant', 'sold-out');
        if (needsSelection) {
          btn.classList.add('needs-variant');
          btn.disabled = false;
        } else if (!canPurchase) {
          btn.classList.add('sold-out');
          btn.disabled = true;
        } else {
          btn.disabled = false;
        }
      });

      if (this.atcText) {
        if (variant && !variant.available) {
          this.atcText.textContent = KROWN.settings.locales.products_sold_out_variant;
        } else {
          this.atcText.textContent = KROWN.settings.locales.products_add_to_cart_button;
        }
      }

      if (hasVariant && this.drawerMedia && variant) {
        const featured = variant.featured_image || (variant.featured_media && variant.featured_media.preview_image);
        const src = featured && (featured.src || featured.url);
        if (src) {
          let img = this.drawerMedia.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.className = 'variant-drawer__image';
            this.drawerMedia.innerHTML = '';
            this.drawerMedia.appendChild(img);
          }
          if (img.src !== src) {
            img.src = src;
            img.alt = variant.title || '';
          }
        }
      }

      if (this.drawerVariants && typeof this.drawerVariants.updateVariantOptionLabels === 'function') {
        this.drawerVariants.updateVariantOptionLabels();
      }
    }

    open() {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('variant-drawer-open');
      this._ensureVariantIdInput();
      this._updateUi();
      this.querySelector('.variant-drawer__close')?.focus();
      document.addEventListener('keydown', this._boundKeydown);
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      this.hidden = true;
      document.body.classList.remove('variant-drawer-open');
      document.removeEventListener('keydown', this._boundKeydown);
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      }
    }

    _onAtcClick() {
      this._addToCart({ redirectToCheckout: false });
    }

    _onBuyNowClick() {
      this._addToCart({ redirectToCheckout: true });
    }

    async _addToCart({ redirectToCheckout = false } = {}) {
      if (this._needsVariantSelection()) {
        return false;
      }

      const variant = this._getActiveVariant();
      if (!variant || !variant.available || !this.drawerForm) {
        return false;
      }

      this._syncFormVariantId();
      const idInput = this.drawerForm.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) {
        return false;
      }

      const btn = redirectToCheckout ? this.buyNowBtn : this.atcBtn;
      btn?.classList.add('working');

      try {
        const response = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: this._serializeForm(this.drawerForm),
        });
        const data = await response.json();

        if (data.status === 422) {
          return false;
        }

        if (redirectToCheckout) {
          btn?.classList.remove('working');
          window.location.href = '/checkout';
          return true;
        }

        await this._handleCartAddSuccess();
        return true;
      } catch (e) {
        console.log(e);
        return false;
      } finally {
        btn?.classList.remove('working');
      }
    }

    async _handleCartAddSuccess() {
      this.close();

      if (typeof window.opusOpen === "function") {
        window.opusOpen();
        return;
      }

      const cartAction = window.KROWN?.settings?.cart_action;
      const cartUrl = window.KROWN?.settings?.routes?.cart_url || '/cart';

      if (cartAction === 'overlay' && document.getElementById('site-cart-sidebar')) {
        try {
          const text = await fetch('?section_id=helper-cart').then((res) => res.text());
          const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
          const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm')?.innerHTML;
          const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal')?.innerHTML;

          if (cartFormInnerHTML && document.getElementById('AjaxCartForm')) {
            const cartItems = document.getElementById('AjaxCartForm');
            cartItems.innerHTML = cartFormInnerHTML;
            if (typeof cartItems.ajaxifyCartItems === 'function') {
              cartItems.ajaxifyCartItems();
            }
          }

          if (cartSubtotalInnerHTML && document.getElementById('AjaxCartSubtotal')) {
            document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalInnerHTML;
          }

          document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
            const countEl = document.querySelector('#AjaxCartForm [data-cart-count]');
            if (countEl) {
              elm.textContent = countEl.textContent;
            }
          });
          document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
            const totalEl = document.querySelector('#AjaxCartForm [data-cart-total]');
            if (totalEl) {
              elm.textContent = totalEl.textContent;
            }
          });

          if (typeof window.opusOpen === "function") {
						window.opusOpen();
						return;
					}

          document.getElementById('site-cart-sidebar')?.show?.();
        } catch (e) {
          console.log(e);
          this.atcBtn?.classList.remove('working');
          this.buyNowBtn?.classList.remove('working');
          window.location.href = cartUrl;
        }
        return;
      }

      this.atcBtn?.classList.remove('working');
      this.buyNowBtn?.classList.remove('working');
      window.location.href = cartUrl;
    }

    _serializeForm(form) {
      const arr = [];
      Array.prototype.slice.call(form.elements).forEach((field) => {
        if (!field.name || field.disabled || ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1) {
          return;
        }
        if (field.type === 'checkbox' || field.type === 'radio') {
          if (!field.checked) {
            return;
          }
        }
        arr.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`);
      });
      return arr.join('&');
    }
  }

  if (typeof customElements.get('quick-variant-drawer') === 'undefined') {
    customElements.define('quick-variant-drawer', QuickVariantDrawer);
  }
}

if (typeof QuickProductDrawerHost !== 'function') {

  class QuickProductDrawerHost {

    static get host() {
      if (!this._host) {
        this._host = document.getElementById('quick-product-drawer-host');
        if (!this._host) {
          this._host = document.createElement('div');
          this._host.id = 'quick-product-drawer-host';
          document.body.appendChild(this._host);
        }
      }
      return this._host;
    }

    static async open(productUrl, trigger) {
      const url = `${productUrl}${productUrl.includes('?') ? '&' : '?'}view=variant-drawer`;
      trigger?.classList.add('working');

      try {
        const response = await fetch(url);
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const root = doc.querySelector('#product-quick-drawer');

        if (!root) {
          return;
        }

        QuickProductDrawerHost.host.innerHTML = root.innerHTML;

        const drawer = QuickProductDrawerHost.host.querySelector('quick-variant-drawer');
        if (drawer) {
          requestAnimationFrame(() => drawer.open());
        }
      } catch (e) {
        console.log(e);
      } finally {
        trigger?.classList.remove('working');
      }
    }
  }

  window.QuickProductDrawerHost = QuickProductDrawerHost;
}
